import React from "react";

import { View, ScrollView, KeyboardAvoidingView } from "react-native";

import moment from "moment";
import { find } from "lodash";

import Utils, {
  navigate,
  hasItems,
  isWeb,
  getElementModel,
  getStorageKey,
} from "../../../../helpers/utils";

import AddJournalNotes from "./AddJournalNotes";
import appStyles from "../../../appStyles";
import EditReflectionBubbleChart from "./EditReflectionBubbleChart";
import EditReflectionFabButton from "./EditReflectionFabButton";
import EditReflectionGemsList from "./EditReflectionGemsList";
import MenuEditOptionsPanel from "../../common/MenuEditOptionsPanel";
import OnboardingConfirmDialog from "../../onboarding/OnboardingConfirmDialog";
import ReflectionCards from "./ReflectionCards";
import ReflectionEditHeader from "./ReflectionEditHeader";
import Search from "../../common/search-bar/Search";
import SelectedInterval from "./SelectedInterval";
import ReflectionHelper from "../ReflectionHelper";
import TutorialsHelper from "../../tutorials/TutorialsHelper";

import appPages from "../../../../appPages";
import SetupConfig from "../../home/cards/onboarding/SetupConfig";
import SettingsHelper from "../../more/SettingsHelper";
import EditReflectionChart from "./EditReflectionChart";
import ComparisonContent from "../../home/cards/reflection/ComparisonContent";

import TUTORIALS_DATA from "../../tutorials/data";
import JournalDialog from "../../common/JournalDialog";
import ContextMenu from "../../common/ContextMenu";
import AppTheme from "../../AppTheme";
import CloseIcon from "../../common/dialog/CloseIcon";
import Icons from "../../common/icons";
import ElementsHelper from "../../elements/ElementsHelper";
import ReflectionStatsCard from "./ReflectionStatsCard";

const DATE_FORMAT = "YYYY-MM-DD 00:00:00.0";
const SHOW_CHILDREN_GEMS = true;

export default class ReflectionEditPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.listRef = null;
    this.selectedGoalElem = null;

    this.barchartStorageKey = getStorageKey(this.controller, "SHOW_BAR_CHART");

    this.state = {
      isLoading: true,
      selectedGem: null,
      showManaMindful: false,
      openConfirmation: false,
      isSearchActive: false,
      hideActions: false,
      showLineChart: false,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.refreshPage();
        },
      );
    } else {
      this.refreshPage();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  addChildrenGems = (node, result, parsed_items) => {
    if (node && node.children) {
      node.children.forEach((c) => {
        if (c.timeline && c.timeline.gems && c.timeline.gems.length > 0) {
          c.timeline.gems.forEach((g) => {
            if (!parsed_items.has(g.gem_id)) {
              parsed_items.add(g.gem_id);
              result.push(g);
            }
          });
        }
        this.addChildrenGems(c, result, parsed_items);
      });
    }
  };

  refreshPage = async () => {
    const { reflection, fetchFromApi, routineHours } = this.getArgs() || {};

    if (!reflection) {
      this.controller.changePage("rn:sets_reflection");
      return;
    }

    const showLineChart = await SettingsHelper.loadData(
      this.controller,
      this.barchartStorageKey,
    );

    reflection.element = ElementsHelper.findById(
      this.controller,
      reflection.edit_element_id,
    );

    // From ElementEdit page
    if (fetchFromApi) {
      this.setState(
        (state) => ({ ...state, reflection, reflectionHours: {} }),
        () => {
          this.loadReflectionDetails({
            reflectionDate: reflection.edit_day,
            time_unit: reflection.edit_time_unit,
          });
        },
      );
    } else {
      // Group hours + days + gems by day
      const reflectionHours = this._groupHoursByDate(reflection.timeline);
      const timelineGems = reflection?.timeline?.gems || [];
      const gems = timelineGems.slice();

      if (SHOW_CHILDREN_GEMS && reflection?.children) {
        const parsed_items = new Set();
        this.addChildrenGems(reflection, gems, parsed_items);
      }

      this.setState(
        (state) => ({
          ...state,
          isLoading: false,
          reflection,
          gems,
          reflectionHours,
          routineHours,
          showLineChart,
        }),
        () => {
          // Tutorials
          this.initTutorials();

          // Load Stats
          this.getStatsCardData();
        },
      );
    }
  };

  _groupHoursByDate = (timeline) => {
    if (!timeline) return {};

    const { days, gems, hours } = timeline;

    let hoursModel = {};
    const gemsByDate = {};
    if (gems != null) {
      gems.forEach((g) => {
        if (g.day != null) gemsByDate[g.day.substring(0, 10)] = g;
      });
    }
    days.forEach((selectedDate, index) => {
      let gem = gemsByDate[selectedDate];
      hoursModel[selectedDate] = {
        date: selectedDate,
        hours: hours ? hours[index] : [],
        gem,
        index,
      };
    });

    return hoursModel;
  };

  // ------------------------------------- SAVE -------------------------------------
  saveNotes = (model) => {
    const { reflection } = this.state;
    const {
      timeline: { gems },
    } = reflection;

    if (!model) return;

    // Update gem's notes and mood
    const selectedDay = moment(reflection.edit_day).format(DATE_FORMAT);
    const gem = reflection.edit_id
      ? find(gems, (g) => g.id == reflection.edit_id)
      : find(gems, (g) => g.day == selectedDay); // Periods

    // UPDATE
    if (gem) {
      gem.mood = model.edit_gem_mood;
      gem.text = model.edit_notes;
    }

    this.setState(
      (state) => ({
        ...state,
        reflectionHours: this._groupHoursByDate(reflection.timeline),
        reflection: {
          ...reflection,
          ...model,
          timeline: {
            ...reflection.timeline,
            gems,
          },
        },
      }),
      () => this.autoSaveReflection(),
    );
  };

  autoSaveReflection = () => {
    const { reflection, showTutorials, isAverageWeek, tutorialStep } =
      this.state;

    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));

    this.controller.tracking.track("Edit Gem");
    const action = {
      action: "save",
      target: "gem",
      item_id: reflection.edit_element_id,
      time_unit: reflection.edit_time_unit,
      day: reflection.edit_day,
      item_type: reflection.edit_object,
      gem_id: reflection.edit_id,
      hours: reflection.target_hours,
      note: reflection.edit_notes,
      mood: reflection.edit_gem_mood,
    };

    console.log("Save Reflection");
    this.controller.cardActionWithCallback(action, () => {
      // Save current step in settings for checklist display
      SettingsHelper.updateSetupProgress(this.controller, {
        [SetupConfig.reflection.name]: true,
      });

      // On Create redirect to Reflection Home
      if (!action.gem_id && !showTutorials) {
        this.controller.goBack();
      } else {
        // Reflection tutorial
        if (showTutorials && tutorialStep == 2) {
          return this.controller.changePage("rn:sets_reflection", {
            args: { tutorialDone: true },
          });
        }

        // Reflection tutorial: On mood selected enable journal button
        if (!isAverageWeek) this.loadTutorial(tutorialStep + 1);

        this.setState((state) => ({
          ...state,
          isLoading: false,
          showManaMindful: !showTutorials,
        }));

        setTimeout(() => {
          this.setState((state) => ({
            ...state,
            showManaMindful: false,
          }));
        }, 5000);
      }
    });
  };

  // ------------------------------------- Edit Journal Entries -------------------------------------
  toggleEditMenuOptions = (item) => {
    this.setState((state) => ({
      ...state,
      selectedGem: item,
    }));
  };

  editJournalEntry = () => {
    const {
      selectedGem: { id, time_unit, day, mood, text, hours },
      reflection,
    } = this.state;

    this.toggleEditMenuOptions(null);

    this.setState(
      (state) => ({
        ...state,
        reflection: {
          ...reflection,
          edit_id: id || "",
          edit_day: day.split(" ")[0],
          edit_time_unit: time_unit,
          edit_gem_mood: mood || "",
          edit_notes: text || "",
          target_hours: hours,
          timeline: reflection.timeline,
          autoFocus: true, // Autofocus on notes textbox only on edit gem
        },
      }),
      () => {
        this.listRef.scrollTo({ y: isWeb ? 0 : 300 });
      },
    );
  };

  toggleDeleteDialog = (isOpen = false) => {
    if (isOpen) {
      this.setState((state) => ({ ...state, openConfirmation: true }));
    } else {
      this.setState((state) => ({
        ...state,
        openConfirmation: false,
        selectedGem: null,
      }));
    }
  };

  deleteJournal = () => {
    const { selectedGem, gems: oldGems } = this.state;
    if (!selectedGem) return;

    const gems = oldGems.filter((g) => g.id !== selectedGem.id);

    this.toggleDeleteDialog(false);

    this.controller.tracking.track("Archive journal");
    const action = {
      action: "save",
      target: "gem",
      gem_id: selectedGem.id,
      gem_action: "remove",
    };
    this.controller.cardActionWithCallback(action, () => {
      this.setState((state) => ({
        ...state,
        isLoading: false,
        showManaMindful: false,
        gems,
      }));
    });
  };

  // ------------------------------------- COMPARISON -------------------------------------
  showToggleChartMenu = (e) => {
    const y = e.clientY || e.nativeEvent?.pageY;
    const x = e.clientX || e.nativeEvent?.pageX;

    this.setState((state) => ({
      ...state,
      context: { x, y },
    }));
  };

  toggleChartMenu = (option) => {
    if (option == "Cancel") {
      return this.setState((state) => ({ ...state, context: null }));
    }

    const showLineChart = option == "Line Graph Mode";
    SettingsHelper.storeData(
      this.controller,
      this.barchartStorageKey,
      showLineChart,
    );

    this.setState((state) => ({
      ...state,
      showLineChart,
      context: null,
    }));
  };

  _getReflectionGoal = (reflection) => {
    const { edit_element_id, edit_day, edit_time_unit } = reflection;

    const resolutions = this.controller.state.resolutions || {};

    //find goal for the current time unit
    let currentGoal = find(
      resolutions.items,
      (it) =>
        it.target_id == edit_element_id &&
        edit_time_unit == it.time_unit &&
        (it.from == null || edit_day >= it.from) &&
        (it.to == null || edit_day <= it.to),
    );

    //find and adjust goal for any time unit
    if (!currentGoal) {
      currentGoal = find(
        resolutions.items,
        (it) =>
          it.target_id == edit_element_id &&
          (it.from == null || edit_day >= it.from) &&
          (it.to == null || edit_day <= it.to),
      );
    }

    return currentGoal;
  };

  // ------------------------------------- STATS CARD -------------------------------------
  getStatsCardData = () => {
    const { reflection } = this.state;
    const { edit_day, edit_time_unit } = reflection;

    const reflectionDate = moment(edit_day)
      .add(1, edit_time_unit)
      .format("YYYY-MM-DD");

    this.controller.getApiData("cards_v2", { reflectionDate }, (cards) => {
      const statsCardData = (cards || []).find((c) => c.type == "stats_card");
      this.setState((state) => ({ ...state, statsCardData }));
    });
  };

  toggleJournalDialog = (gem, refresh = false, isStatsCard = false) => {
    const { reflection } = this.state;

    // Close dialog
    if (!gem) {
      // Refresh after closing stats journal dialog
      if (this.state.isStatsCard) {
        this.setState((state) => ({ ...state, selectedGoalElem: null }));
        return this.getStatsCardData();
      }

      // After update
      if (refresh) {
        this.loadReflectionDetails({
          reflectionDate: reflection.edit_day,
          time_unit: reflection.edit_time_unit,
        });
      } else {
        // Simple dialog close
        this.setState((state) => ({ ...state, selectedGoalElem: null }));
      }

      return;
    }

    const selectedGoalElem = isStatsCard
      ? gem
      : {
          ...gem,
          item_id: reflection.edit_element_id,
          icon: reflection.edit_icon,
          name: reflection.edit_name,
          time_unit: reflection.edit_time_unit,
          day: reflection.edit_day,
          note: gem.text,
        };

    this.setState((state) => ({
      ...state,
      selectedGoalElem,
      isStatsCard,
    }));
  };

  onDateChanged = (data) => {
    const { reflection, reflectionHours } = this.state;

    const { date: newDate, value: target_hours } = data;
    if (newDate == reflection.edit_day) return;

    // Mood & text for selected date
    const gem = reflectionHours[newDate]
      ? reflectionHours[newDate].gem || {}
      : {};

    this.setState((state) => ({
      ...state,
      reflection: {
        ...reflection,
        edit_day: newDate,
        edit_gem_mood: gem.mood || "",
        edit_notes: gem.text || "",
        target_hours,
      },
    }));
  };

  // ------------------------------------- Interval -------------------------------------
  onIntervalChanged = (data) => {
    const { reflection } = this.state;
    const { id, time_unit, day, mood, text, hours } = data;

    const newReflection = {
      ...reflection,
      edit_id: id || "", // needs id for JOURNAL new notes (gem)
      edit_day: day,
      edit_time_unit: time_unit,
      edit_gem_mood: mood || "",
      edit_notes: text || "",
      target_hours: hours,
      timeline: reflection.timeline,
    };

    this.setState(
      (state) => ({
        ...state,
        reflection: newReflection,
      }),
      () => {
        // Refresh stats info
        this.getStatsCardData();
      },
    );
  };

  loadReflectionDetails = (params) => {
    const { reflection } = this.state;

    // Tutorials
    this.initTutorials(true);

    this.setState((state) => ({
      ...state,
      isLoading: true,
      selectedGoalElem: null,
    }));
    this.controller.getApiData("reflection", params, (reflectionData) => {
      const {
        header: { days, timeline },
        children,
      } = reflectionData;

      // By id
      let currentReflection = this._findCurrentReflection(children) || {};
      const goal = currentReflection.goal;

      // Tutorials
      if (!reflection.edit_gem_mood) {
        const reflectionDay = moment(reflection.edit_day).format(DATE_FORMAT);
        const gem = find(timeline.gems || [], (g) => g.day == reflectionDay);

        if (gem) {
          reflection.edit_gem_mood = gem.mood;
          reflection.edit_notes = gem.text;
        }
      }

      let newReflectionModel = {};
      if (hasItems(days)) {
        newReflectionModel.edit_day = days[0].gem_day;
      }

      // Top Core Elements for bubble chart
      if (reflection.edit_object == "time") {
        const { core_elements, days: setDays } =
          ReflectionHelper.addCoreElementsHours(children);

        newReflectionModel.core_elements = core_elements;

        // For interval navigation
        currentReflection.timeline = {
          ...timeline,
          days: setDays, // Period has empty days from api
        };
      }

      this.setState((state) => ({
        ...state,
        isLoading: false,
        reflectionHours: this._groupHoursByDate(currentReflection.timeline),
        gems: timeline.gems,
        reflection: {
          ...reflection,
          ...newReflectionModel,
          goal,
          edit_time_unit: params.time_unit,
          timeline: currentReflection.timeline,
          //edit_gem_mood: "",
          //edit_notes: "",
          //target_hours: 10,
        },
      }));
    });
  };

  _findCurrentReflection = (children) => {
    if (!hasItems(children)) return null;

    let found = find(
      children,
      (c) => c.item_id == this.state.reflection.edit_element_id,
    );

    if (found) return found;

    children.forEach((c) => {
      const childReflection = this._findCurrentReflection(c.children);
      if (childReflection) found = childReflection;
    });

    return found;
  };

  // ------------------------------------- FAB -------------------------------------
  editElement = () => {
    const { reflection } = this.state;

    const selectedElement = find(
      this.controller.state.elements,
      (a) => a.id == reflection.edit_element_id,
    );

    navigate(this.controller, "rn:edit_element", "changepage", {
      element: getElementModel(selectedElement),
      reflection,
    });
  };

  addNewGoal = () => {
    const { reflection } = this.state;

    navigate(this.controller, "resolution_detail", "changepage", {
      reflection,
      goal: {
        weekdays: "1111111",
        operator: "more than",
        importance: 5,
        time_unit: reflection.edit_time_unit || "week",
        target_id: reflection.edit_element_id,
        target_name: reflection.edit_name,
        target_type: reflection.edit_object,
        target_emoji: reflection.edit_icon,
      },
    });
  };

  // ------------------------------------- SEARCH -------------------------------------
  toggleSearch = () => {
    this.setState((state) => ({
      ...state,
      isSearchActive: !state.isSearchActive,
    }));
  };

  filterResults = ({ term }) => {
    const {
      reflection: {
        timeline: { gems },
      },
    } = this.state;
    let filteredGems = [...gems];

    if (term) {
      const pattern = Utils.getSearchPattern(term);

      filteredGems = filteredGems.filter((g) => {
        return g.text && g.text.match(pattern);
      });
    }

    this.setState((state) => ({
      ...state,
      isLoading: false,
      gems: filteredGems,
    }));
  };

  goBack = (reflect = false) => {
    const { showTutorials, tutorialStep } = this.state;

    if (!reflect && showTutorials && tutorialStep > 1) {
      this.loadTutorial(tutorialStep - 1);
    } else {
      this.controller.goBack();
    }
  };

  // ------------------------------------- TUTORIALS -------------------------------------
  initTutorials = (isAverageWeek = false) => {
    const showTutorials = (this.controller?.state?.showTutorials || {})[
      TutorialsHelper.STORAGE_KEYS.reflection
    ];

    if (showTutorials) {
      const tutoriaData = isAverageWeek
        ? TUTORIALS_DATA["average_week_details"]
        : TUTORIALS_DATA["reflection_edit"];

      this.setState(
        (state) => ({ ...state, tutoriaData, isAverageWeek }),
        () => {
          this.loadTutorial(1);
        },
      );
    } else {
      this.setState((state) => ({
        ...state,
        mariMessage: "",
        tutorialStep: 0,
        showTutorials: false,
        tutorialData: [],
      }));
    }
  };

  loadTutorial = (step = 1) => {
    const { tutoriaData } = this.state;
    if (!tutoriaData || !tutoriaData[step - 1]) return;

    const tutorial = step >= 0 ? tutoriaData[step - 1] || {} : {};

    this.setState((state) => ({
      ...state,
      showTutorials: true,
      tutorialStep: step,
      hideActions: !!step,
      mariMessage: tutorial.message,
      mariAction: tutorial.action
        ? {
            text: tutorial.action.text,
            onPress: () => {
              this.loadTutorial(step + 1);
            },
          }
        : {},
    }));
  };

  onMoodSelected = () => {
    const { showTutorials, isAverageWeek } = this.state;
    if (!showTutorials || isAverageWeek) return;

    this.loadTutorial(2);
  };

  // ------------------------------------- ROUTINES -------------------------------------
  createNewRoutine = (type) => {
    const { routineHours } = this.state;

    navigate(this.controller, "rn:edit_routine", "changepage", {
      routine: { time_interval: type, hours: JSON.stringify(routineHours) },
    });
  };

  // ------------------------------------- REPORTS -------------------------------------
  createNewReport = () => {
    const { reflection } = this.state;
    const { edit_time_unit, edit_day } = reflection;

    navigate(this.controller, appPages.reportsPage, "changepage", {
      args: {
        selectedDate: edit_day,
        timeUnit: edit_time_unit,
      },
    });
  };

  render() {
    const {
      isLoading,
      reflection,
      reflectionHours,
      openConfirmation,
      isSearchActive,
      hideActions,
      tutorialStep,
      mariMessage,
      mariAction,
      showTutorials,
      isAverageWeek,
      gems,
      showLineChart,
      context,
      selectedGoalElem,
      statsCardData,
    } = this.state;
    if (!reflection) return null;

    const { edit_time_unit, edit_object, edit_day } = reflection;

    // Goal
    const currentGoal = this._getReflectionGoal(reflection);

    const lifeTimeAverage = ReflectionHelper.getReflectionAverageHours(
      reflectionHours,
      reflection.edit_day,
    );

    const reflectionElement = {
      id: reflection.edit_element_id,
      icon: reflection.edit_icon,
      name: reflection.edit_name,
    };

    return (
      <View style={appStyles.container}>
        {context ? (
          <ContextMenu
            showSnoozed={true}
            context={context}
            onSnooze={this.toggleChartMenu}
            options={[
              {
                name: showLineChart ? "Bar Graph Mode" : "Line Graph Mode",
                icon: (
                  <Icons.BarChartSwitchViewIcon showLineChart={showLineChart} />
                ),
              },
              {
                name: "Cancel",
                icon: (
                  <CloseIcon
                    color={AppTheme.almostBlack}
                    opacity={1}
                    style={{
                      position: "relative",
                      top: "unset",
                      right: "unset",
                    }}
                  />
                ),
              },
            ]}
          />
        ) : null}

        {/* Journal dialog for Top Comparison Card and Stats Card */}
        {selectedGoalElem ? (
          <JournalDialog
            type={"reflection"}
            item={selectedGoalElem}
            controller={this.controller}
            onJournalComplete={() => this.toggleJournalDialog(null, true)}
            close={() => this.toggleJournalDialog(null)}
          />
        ) : null}

        <OnboardingConfirmDialog
          isOpen={openConfirmation}
          title="Are you sure?"
          description="Tapping confirm will remove this journal entry"
          onConfirm={() => this.deleteJournal()}
          onClose={() => this.toggleDeleteDialog(false)}
        />

        <MenuEditOptionsPanel
          isPanelActive={!!this.state.selectedGem}
          close={() => this.toggleEditMenuOptions(null)}
          options={[
            {
              text: "Edit Journal Entry",
              action: this.editJournalEntry,
            },
            {
              text: "Delete Journal Entry",
              action: () => this.toggleDeleteDialog(true),
              isDelete: true,
            },
          ]}
        />

        <View>
          <ReflectionEditHeader
            controller={this.controller}
            reflection={reflection}
            isLoading={isLoading}
            highlightBack={isAverageWeek && tutorialStep == 3}
            onGoBack={this.goBack}
            createNewReport={this.createNewReport}
          />

          <KeyboardAvoidingView
            style={appStyles.kbView}
            behavior="position"
            keyboardVerticalOffset={-150}
          >
            <ScrollView
              ref={(el) => (this.listRef = el)}
              style={[
                appStyles.scrollView,
                {
                  backgroundColor: "#FFF",
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                },
              ]}
              contentContainerStyle={appStyles.scrollViewContent}
            >
              {/* Interval */}
              <SelectedInterval
                controller={this.controller}
                reflection={reflection}
                reflectionHours={reflectionHours}
                onIntervalChanged={this.onIntervalChanged}
                showTutorials={showTutorials}
              />

              {/* Reflection bubble chart */}
              {reflection.edit_object == "time" ? (
                <EditReflectionBubbleChart
                  reflection={reflection}
                  saveNotes={this.saveNotes}
                />
              ) : (
                <>
                  {showLineChart ? (
                    <EditReflectionChart
                      reflection={reflection}
                      reflectionHours={reflectionHours}
                      reflectionElement={reflectionElement}
                      controller={this.controller}
                      showToggleChartMenu={this.showToggleChartMenu}
                    />
                  ) : (
                    <ComparisonContent
                      selectedDate={reflection.edit_day}
                      timeUnit={edit_time_unit}
                      data={{
                        goal: currentGoal,
                        element: reflectionElement,
                        reflection_hours: reflectionHours,
                        time_unit: reflection.edit_time_unit,
                        hours: reflection.target_hours, // hours completed
                        averages: {
                          lifetime: lifeTimeAverage,
                        },
                      }}
                      onDateChanged={this.onDateChanged}
                      showJournalDialog={this.toggleJournalDialog}
                      showToggleChartMenu={this.showToggleChartMenu}
                    />
                  )}

                  <ReflectionCards
                    reflection={reflection}
                    reflectionHours={reflectionHours}
                    goal={reflection.goal}
                    lifeTimeAverage={lifeTimeAverage}
                  />

                  {/* Input notes */}
                  <AddJournalNotes
                    reflection={reflection}
                    reflectionHours={reflectionHours}
                    saveNotes={this.saveNotes}
                    showTutorials={showTutorials && !isAverageWeek}
                    tutorialStep={tutorialStep}
                    onMoodSelected={this.onMoodSelected}
                  />
                </>
              )}

              {/* Stats */}
              <ReflectionStatsCard
                controller={this.controller}
                statsCardData={statsCardData}
                timeUnit={edit_time_unit}
                selectedDate={edit_day}
                parentElement={reflectionElement}
                toggleJournalDialog={this.toggleJournalDialog}
                changePage={(page, args) => {
                  this.props.navigation.push(page, args);
                }}
              />

              {/* Journal Entries */}
              <EditReflectionGemsList
                gems={gems}
                goal={reflection.goal}
                element={reflection.element}
                timeUnit={edit_time_unit}
                editGemId={reflection.edit_id}
                toggleEditMenuOptions={this.toggleEditMenuOptions}
                toggleSearch={this.toggleSearch}
                controller={this.controller}
              />
            </ScrollView>

            <EditReflectionFabButton
              style={{ bottom: showTutorials ? 0 : 58 }}
              showManaMindful={this.state.showManaMindful}
              addNewGoal={this.addNewGoal}
              editElement={this.editElement}
              timeUnit={edit_time_unit}
              showLineChart={showLineChart}
              createNewRoutine={
                edit_object == "time"
                  ? () => this.createNewRoutine(edit_time_unit)
                  : null
              }
              createNewReport={
                edit_time_unit == "week" ? this.createNewReport : null
              }
              toggleChartMenu={this.toggleChartMenu}
              mariOptions={{
                mariMessage,
                mariAction,
                hideActions,
                tutorialStep,
                showTutorials,
              }}
            />

            {isSearchActive ? (
              <Search
                showIcon={false}
                placeholder="Search Journal Entries"
                controller={this.controller}
                filterResults={this.filterResults}
                style={isWeb ? {} : { bottom: 80 }}
              />
            ) : null}
          </KeyboardAvoidingView>
        </View>
      </View>
    );
  }
}
